/**
 * assembleItemService
 *
 * Wrapper for the assemble_item Supabase RPC (migration 373).
 *
 * Returns the fully composed state of a single Item as a typed object.
 * This is the canonical agent input envelope and the debug inspection
 * surface for item state.
 *
 * The RPC is the only interface — no direct table access from this module.
 * All sections are assembled defensively server-side; missing related
 * records produce null or empty arrays, never errors.
 */

import { getSupabaseClient } from './supabaseClient';

// ─────────────────────────────────────────────────────────────────────────────
// Types — mirroring the JSONB shape returned by the assemble_item RPC
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Classification state of an item (fourth orthogonal state machine).
 * See Canon §7 in 03-state-model-and-transitions.md.
 */
export type ClassificationStatus = 'empty' | 'proposed' | 'approved';

/** Who proposed a classification. */
export type ClassificationProposer = 'agent' | 'user';

/** Normalized review lifecycle for a classification proposal. */
export type ClassificationReviewStatus =
  | 'pending_review'
  | 'applied'
  | 'overridden'
  | 'rejected';

/** Raw status stored in item_classification_proposals. */
export type ClassificationProposalStatus =
  | 'active'
  | 'accepted'
  | 'superseded'
  | 'rejected';

/**
 * A single classification proposal record.
 * Proposals are append-only — every determination event is preserved.
 */
export interface AssembledClassificationProposal {
  id: string;
  proposed_by: ClassificationProposer;
  proposed_by_agent: string | null;
  proposed_by_user_id: string | null;
  /** Raw JSONB: { category, altitude_level, question_responses, rationale, confidence } */
  classification_fields: Record<string, unknown>;
  status: ClassificationProposalStatus;
  /** Normalized label for agent consumers. */
  review_status: ClassificationReviewStatus;
  accepted_by_user_id: string | null;
  accepted_at: string | null;
  created_at: string;
  /** Extracted from classification_fields for convenience. */
  confidence: string | null;
  rationale: string | null;
  category: string | null;
  altitude: string | null;
}

/** Section 2: Classification state + current proposals. */
export interface AssembledClassification {
  status: ClassificationStatus;
  item_category: string | null;
  altitude_level: number | null;
  active_proposal: AssembledClassificationProposal | null;
  accepted_proposal: AssembledClassificationProposal | null;
}

/**
 * A member of an item within a relationship group, excluding this item.
 */
export interface AssembledGroupMember {
  item_id: string;
  title: string | null;
  membership_role: string | null;
}

/**
 * An edge-native typed relationship involving this item.
 *
 * Schema note: relationship_type is one of:
 *   'related' | 'dependency' | 'aggregation' | 'elaboration' | 'breakdown'
 *
 * The subtype column provides additional specificity (e.g. 'hard', 'soft'
 * for dependency edges). The six-type vocabulary requested (depends_on,
 * enables, etc.) has no direct column mapping — see _schema_gaps.
 *
 * declared_mode:
 *   'declared'  = human-created relationship
 *   'emergent'  = agent-suggested relationship
 *
 * confidence: extracted from metadata when present (agent-suggested edges).
 */
export interface AssembledRelationshipEdge {
  id: string;
  relationship_type: 'related' | 'dependency' | 'aggregation' | 'elaboration' | 'breakdown' | 'carries';
  subtype: string | null;
  /**
   * Carries only (canon 12 §5.11, migration 968): the share of the target's worth
   * the source carries, 0–1. Null on every other type. numeric — may arrive as text.
   */
  magnitude?: number | string | null;
  /** Whether this item is the source or target of the edge. */
  this_item_role: 'source' | 'target';
  other_item_id: string;
  other_item_title: string | null;
  other_item_exit_status: string | null;
  other_item_lifecycle_state: string | null;
  status: string;
  declared_mode: 'declared' | 'emergent';
  label: string | null;
  rationale: string | null;
  metadata: Record<string, unknown>;
  confidence: string | null;
  created_at: string;
  created_by: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

/** A group (cluster, package, related_group, collection) this item belongs to. */
export interface AssembledGroupMembership {
  group_id: string;
  group_type: 'cluster' | 'package' | 'related_group' | 'collection';
  group_subtype: string | null;
  group_status: string;
  group_title: string | null;
  declared_mode: 'declared' | 'emergent';
  membership_role: string | null;
  inclusion_reason: string | null;
  added_at: string;
  other_members: AssembledGroupMember[];
}

/** Section 3: All active relationships. */
export interface AssembledRelationships {
  edges: AssembledRelationshipEdge[];
  group_memberships: AssembledGroupMembership[];
}

/** A governance event recorded against this item. */
export interface AssembledGovernanceEvent {
  id: string;
  event_type: string;
  occurred_at: string;
  actor_id: string | null;
  actor_name: string | null;
  from_state: string | null;
  to_state: string | null;
  note: string | null;
  rationale: string | null;
  before_state_snapshot: Record<string, unknown> | null;
  after_state_snapshot: Record<string, unknown> | null;
  cycle_id: string | null;
  session_id: string | null;
  loop_id: string | null;
  priority_set_id: string | null;
}

/** An audit event recorded against this item. */
export interface AssembledAuditEvent {
  id: string;
  event_type: string;
  created_at: string;
  created_by: string | null;
  payload: Record<string, unknown> | null;
}

/** A lineage event (split, merge, supersede, retire, fork) involving this item. */
export interface AssembledLineageEvent {
  id: string;
  event_type: string;
  source_item_ids: string[];
  target_item_ids: string[];
  rationale: string | null;
  performed_by: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  /** Whether this item was the source or a produced result. */
  this_item_role: 'source' | 'target';
}

/** Section 5: Full provenance log. */
export interface AssembledProvenance {
  governance_events: AssembledGovernanceEvent[];
  audit_events: AssembledAuditEvent[];
  lineage_events: AssembledLineageEvent[];
}

/** Catchball loop context from the active cycle. */
export interface AssembledActiveLoop {
  loop_id: string;
  loop_number: number;
  loop_state: string;
  ball_holder_scope: string | null;
  ball_holder_user_id: string | null;
  request_issued_at: string | null;
  proposal_submitted_at: string | null;
}

/** Section 6: Cycle context for this item, or null if not in an active cycle. */
export interface AssembledCycleContext {
  cycle_id: string;
  cycle_name: string;
  cycle_status: string;
  cycle_phase: string;
  participation: {
    participation_status: string;
    inclusion_reason: string;
    added_at: string | null;
    notes: string | null;
  };
  active_loop: AssembledActiveLoop | null;
}

/**
 * An unresolved flag / pending review request against this item.
 * Sourced from item_mutation_reviews where decision = 'deferred'.
 */
export interface AssembledOpenFlag {
  id: string;
  mutation_type: string;
  affected_fields: unknown[];
  approval_level_required: 'standard' | 'intermediate' | 'admin';
  ring_at_time: 1 | 2 | 3;
  requested_by: string | null;
  requested_at: string;
  decision: 'deferred';
  session_disposition: string | null;
  reason: string | null;
  cycle_id: string | null;
  track_id: string | null;
  session_id: string | null;
}

/** Schema gaps documented inline by the RPC. */
export interface AssembledSchemaGaps {
  relationship_type_vocabulary: string;
  agent_annotations_table: string;
  confidence_on_edges: string;
}

/**
 * The fully assembled Item object returned by the assemble_item RPC.
 *
 * This is the canonical input envelope for agents: read from this object,
 * write output back to the appropriate lineage record.
 *
 * core: raw items row — use for all scalar fields
 * classification: current classification state + proposal history
 * relationships: edge and group memberships (active only)
 * classification_proposals: append-only history (includes agent annotations)
 * provenance: ordered event log across all provenance sources
 * cycle_context: current active cycle participation + catchball position
 * open_flags: unresolved protected mutation reviews
 */
export interface AssembledItem {
  id: string;
  /** All scalar columns from the items row. */
  core: Record<string, unknown>;
  classification: AssembledClassification;
  relationships: AssembledRelationships;
  /** Full ordered history of classification proposals including agent annotations. */
  classification_proposals: AssembledClassificationProposal[];
  provenance: AssembledProvenance;
  cycle_context: AssembledCycleContext | null;
  open_flags: AssembledOpenFlag[];
  assembled_at: string;
  _schema_gaps: AssembledSchemaGaps;
}

/** Result wrapper — consistent with other service functions in this codebase. */
export type AssembleItemResult =
  | { data: AssembledItem; error: null }
  | { data: null; error: string };

// ─────────────────────────────────────────────────────────────────────────────
// Service function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * assembleItem
 *
 * Calls the assemble_item RPC and returns the fully typed assembled object.
 *
 * Usage:
 *   const { data, error } = await assembleItem(itemId);
 *   if (error) { ... }
 *   // data is AssembledItem
 *
 * Agents should call this once at the start of a run to get full item context,
 * then read the fields relevant to their task. See AGENT_INTEGRATION_NOTE.md
 * in this directory for the full integration contract.
 */
export async function assembleItem(itemId: string): Promise<AssembleItemResult> {
  try {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase.rpc('assemble_item', {
      p_item_id: itemId,
    });

    if (error) {
      return { data: null, error: error.message };
    }

    if (!data) {
      return { data: null, error: 'No data returned from assemble_item' };
    }

    // The RPC returns success:false with an error key on not-found
    if (data && typeof data === 'object' && 'success' in data && data.success === false) {
      return { data: null, error: (data as { error: string }).error ?? 'Item not found' };
    }

    return { data: data as AssembledItem, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err.message : 'Unknown error calling assemble_item',
    };
  }
}
